<template>
    <tailwind-layout title="Applicant Information">
        <template #header>
            <div class="mb-4 px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <h1 class="font-semibold text-2xl leading-tight">Applicant Information</h1>
                <div class="flex items-center space-x-4">
                    <inertia-link class="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10" :href="route('properties.show', property.id)">
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>
        </template>

        <!-- for experian attempts -->
        <div v-if="experian_attempt.attempts < 3" class="mb-6">
            <alert-card type="warning">
                <h3 class="text-sm font-medium text-yellow-800">{{experian_attempt.attempts}} Attempts Remaining</h3>
                The perspective tenant's credit report is only good for 30 days from the date of purchase.
            </alert-card>
        </div>

        <div class="mt-8 mx-auto grid grid-cols-1 gap-6 lg:grid-flow-col-dense lg:grid-cols-12 px-0">
            <div class="lg:col-start-1 lg:col-span-3 relative ">
                <div class="flex flex-col justify-center sticky top-20">
                    <h2 class="font-bold text-gold-900 bg-gold-50 border border-gold-900 uppercase text-center rounded-md py-2 mb-4">Actions</h2>

                    <!-- View-only notice replaces all action items for collaborators -->
                    <div v-if="isSharedViewOnly" class="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                        <p class="font-semibold">View-only access</p>
                        <p class="mt-1 text-blue-600">Only the listing agent can perform actions on this application.</p>
                    </div>

                    <ul v-else role="list" class="flex flex-col text-sm font-semibold text-gray-900 bg-white divide-y-2 divide-gray-200
                        border border-gold-900 *:w-full *:px-3 *:flex *:justify-between *:py-2
                    ">
                        <li v-if="!application.accept_decline && application.agent_application_display !== 0 && application.required_addons.length < addons.length" class="hover:bg-gold-50">
                            <button @click="openEnhancedScreeningOptionsModal()">
                                <font-awesome-icon :icon="faUserCheck" class="pr-1" />
                                Verify this Applicant
                            </button>
                            <tool-tip
                                title="Verify this Applicant"
                                description="RentRisk's enhanced screening verification help you prevent fraud by verifying data listed on a rental application."
                            ></tool-tip>
                        </li>
                        <li v-if="application.required_addons && application.required_addons.length > 0" class="hover:bg-gold-50">
                            <button @click="openEnhancedScreeningResultsModal()">
                                <font-awesome-icon :icon="faShieldCheck" class="pr-1" />
                                Enhanced Screening Results
                            </button>
                            <tool-tip
                                title="Enhanced Screening Results"
                                description="View the results of enhanced screening verifications for this applicant."
                            ></tool-tip>
                        </li>
                        <li v-if="(application.share_id)" class="hover:bg-gold-50">
                            <inertia-link as="a" target="_blank" :href="route('agent.credit-report',application.id)" class="w-full">
                                <font-awesome-icon :icon="faFiles" class="pr-2" />
                                View Credit Report
                            </inertia-link>
                        </li>
                        <li v-if="!application.accept_decline && application.agent_application_display !== 0 && application.is_shared" class="hover:bg-gold-50">
                            <button @click="openAcceptModal(application)">
                                <font-awesome-icon :icon="faCheckCircle" class="pr-2" />
                                Accept Application
                            </button>
                            <tool-tip
                                title="Accept Application"
                                description="Clicking Accept Application will display a popup window with more information for you to review before confirming your decision."
                            ></tool-tip>
                        </li>
                        <li v-if="!application.accept_decline && application.agent_application_display !== 0" class="hover:bg-gold-50">
                            <button @click="openDeclineModal(application)">
                                <font-awesome-icon :icon="faCircleX" class="pr-2" />
                                Decline Application
                            </button>
                            <tool-tip
                                title="Decline Application"
                                description="Clicking Decline Application will display a popup window with more information for you to review before confirming your decision."
                            ></tool-tip>
                        </li>
                        <li class="hover:bg-gold-50 flex flex-col">
                            <Menu as="div" class="relative block">
                                <MenuButton class="w-full focus:z-10">
                                    <div class="flex justify-between items-center">
                                        <div class="">
                                            <span class="sr-only">Open options</span>
                                            <font-awesome-icon :icon="faCloudArrowDown" class="pr-2" />
                                            Downloads
                                        </div>
                                        <div>
                                            <FontAwesomeIcon :icon="faChevronDown" class="ml-2" aria-hidden="true" />
                                        </div>
                                    </div>
                                </MenuButton>
                                <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                                <MenuItems class="absolute right-0 z-10 -mr-1 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                                    <div class="py-1">
                                    <MenuItem class="hover:bg-blue-50">
                                        <a :href="route('properties.download',[property.id, application.id])" class="text-gray-700 block px-4 py-2 text-sm">Application</a>
                                    </MenuItem>
                                        <MenuItem class="hover:bg-blue-50">
                                        <a :href="route('leasing-worksheet.download',[property.id, application.id])" class="text-gray-700 block px-4 py-2 text-sm">Leasing Decision Worksheet</a>
                                    </MenuItem>
                                        <MenuItem class="hover:bg-blue-50">
                                        <a :href="route('properties.download_both',[property.id, application.id])" class="text-gray-700 block px-4 py-2 text-sm">Combined</a>
                                    </MenuItem>
                                    </div>
                                </MenuItems>
                                </transition>
                            </Menu>
                        </li>
                        <li v-if="application.agent_application_display === 0" class="hover:bg-gold-50">
                            <inertia-link :href="route('properties.application.restore',[property.id, application.id])">
                                <font-awesome-icon :icon="faArrowsRotate" class="pr-2" />
                                Restore Application
                            </inertia-link>
                        </li>
                        <li v-if="application.agent_application_display === 1" class="hover:bg-gold-50 hover:rounded-b-lg">
                            <inertia-link :href="route('properties.application.archive',[property.id, application.id])">
                                <font-awesome-icon :icon="faBoxArchive" class="pr-2" />
                                Archive Application
                            </inertia-link>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="space-y-6 lg:col-start-4 lg:col-span-9 bg-white shadow sm:rounded-lg p-8 w-full">
                <div class="-mt-8">
                    <dl class="divide-y divide-gray-300">
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Application Accepted/Declined Status</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <span v-if="application.accept_decline === 1">
                                    Accepted
                                </span>
                                <span v-else-if="application.accept_decline === 2">
                                    Declined Other
                                </span>
                                <span v-else-if="application.accept_decline === 3">
                                    Declined Financial/Credit
                                </span>
                                <span v-else>
                                    N/A
                                </span>
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Application Accepted/Declined Email</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <span v-if="application.accept_decline > 0">
                                    <span class="font-bold">Sent on:</span> {{ application.accept_decline_date }}<br>
                                    <span class="font-bold mt-3">Email Message:</span> <span v-html="application.accept_decline_email"></span>
                                </span>
                                <span v-else>
                                    N/A
                                </span>
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Property Address</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                {{ property.full_address }}
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Full Name</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                {{ application.first_name }} <span v-show="application.middle_name">{{ application.middle_name }}</span> {{ application.last_name }}
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Birth Date</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">{{ application.birthday }}</dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Phone</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <div v-show="application.home_phone">{{ application.home_phone }}</div>
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Email Address</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0"><a :href="'mailto:'+application.user.email">{{ application.user.email }}</a></dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Current Address</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">{{ application.full_address }} <span v-if="application.current_own_rent">({{application.current_own_rent === 1 ? 'Own' : 'Rent'}})</span></dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Current Landlord</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <ul v-if="application.addresses">
                                    <li>
                                        <span class="font-bold">NAME: </span>
                                        <span v-if="application.landlord_name">{{ application.landlord_name }}</span>
                                        <span v-else>N/A</span>
                                    </li>
                                    <li>
                                        <span class="font-bold">PHONE: </span>
                                        <span v-if="application.landlord_phone">{{ application.landlord_phone }}</span>
                                        <span v-else>N/A</span>
                                    </li>
                                    <li><span class="font-bold">EMAIL: </span>
                                        <span v-if="application.landlord_email"><a :href="'mailto:'+application.landlord_email">{{ application.landlord_email }}</a></span>
                                        <span v-else>N/A</span>
                                    </li>
                                    <li v-if="landlordQuestionnaireStatus('current_landlord')">
                                        <span class="font-bold">QUESTIONNAIRE: </span>
                                        <span>{{ landlordQuestionnaireStatus('current_landlord') }}</span>
                                    </li>
                                    <template v-if="landlordVerification('current_landlord')?.submitted_at">
                                        <li><span class="font-bold">INFORMATION ACCURATE: </span><span>{{ formatYesNo(landlordVerification('current_landlord').info_accurate) }}</span></li>
                                        <li><span class="font-bold">PAID ON TIME: </span><span>{{ formatYesNo(landlordVerification('current_landlord').paid_on_time) }}</span></li>
                                        <li><span class="font-bold">CARED FOR PROPERTY: </span><span>{{ formatYesNo(landlordVerification('current_landlord').cared_for_property) }}</span></li>
                                        <li><span class="font-bold">WOULD RENT AGAIN: </span><span>{{ formatYesNo(landlordVerification('current_landlord').would_rent_again) }}</span></li>
                                        <li v-if="landlordVerification('current_landlord').comments"><span class="font-bold">COMMENTS: </span><span>{{ landlordVerification('current_landlord').comments }}</span></li>
                                        <li><span class="font-bold">SUBMITTED: </span><span>{{ formatSubmittedAt(landlordVerification('current_landlord').submitted_at) }}</span></li>
                                    </template>
                                </ul>
                                <span v-else>N/A</span>
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Current Real Estate Agent</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <ul v-if="application.addresses">
                                    <li>
                                        <span class="font-bold">NAME: </span>
                                        <span v-if="application.real_estate_agent_name">{{ application.real_estate_agent_name }}</span>
                                        <span v-else>N/A</span>
                                    </li>
                                    <li>
                                        <span class="font-bold">PHONE: </span>
                                        <span v-if="application.real_estate_agent_phone">{{ application.real_estate_agent_phone }}</span>
                                        <span v-else>N/A</span>
                                    </li>
                                    <li><span class="font-bold">EMAIL: </span>
                                        <span v-if="application.real_estate_agent_email"><a :href="'mailto:'+application.real_estate_agent_email">{{ application.real_estate_agent_email }}</a></span>
                                        <span v-else>N/A</span>
                                    </li>
                                </ul>
                                <span v-else>N/A</span>
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Previous Address</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <span v-if="application.addresses">{{ application.addresses.previous_address }}  <span v-if="application.previous_own_rent">({{application.previous_own_rent === 1 ? 'Own' : 'Rent'}})</span></span>
                                <span v-else>N/A</span>
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Previous Landlord</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <ul v-if="application.addresses">
                                    <li>
                                        <span class="font-bold">NAME: </span>
                                        <span v-if="application.addresses.previous_landlord_name">{{ application.addresses.previous_landlord_name }}</span>
                                        <span v-else>N/A</span>
                                    </li>
                                    <li>
                                        <span class="font-bold">PHONE: </span>
                                        <span v-if="application.addresses.previous_landlord_phone">{{ application.addresses.previous_landlord_phone }}</span>
                                        <span v-else>N/A</span>
                                    </li>
                                    <li><span class="font-bold">EMAIL: </span>
                                        <span v-if="application.addresses.previous_landlord_email"><a :href="'mailto:'+application.addresses.previous_landlord_email">{{ application.addresses.previous_landlord_email }}</a></span>
                                        <span v-else>N/A</span>
                                    </li>
                                    <li v-if="landlordQuestionnaireStatus('previous_landlord')">
                                        <span class="font-bold">QUESTIONNAIRE: </span>
                                        <span>{{ landlordQuestionnaireStatus('previous_landlord') }}</span>
                                    </li>
                                    <template v-if="landlordVerification('previous_landlord')?.submitted_at">
                                        <li><span class="font-bold">INFORMATION ACCURATE: </span><span>{{ formatYesNo(landlordVerification('previous_landlord').info_accurate) }}</span></li>
                                        <li><span class="font-bold">PAID ON TIME: </span><span>{{ formatYesNo(landlordVerification('previous_landlord').paid_on_time) }}</span></li>
                                        <li><span class="font-bold">CARED FOR PROPERTY: </span><span>{{ formatYesNo(landlordVerification('previous_landlord').cared_for_property) }}</span></li>
                                        <li><span class="font-bold">WOULD RENT AGAIN: </span><span>{{ formatYesNo(landlordVerification('previous_landlord').would_rent_again) }}</span></li>
                                        <li v-if="landlordVerification('previous_landlord').comments"><span class="font-bold">COMMENTS: </span><span>{{ landlordVerification('previous_landlord').comments }}</span></li>
                                        <li><span class="font-bold">SUBMITTED: </span><span>{{ formatSubmittedAt(landlordVerification('previous_landlord').submitted_at) }}</span></li>
                                    </template>
                                </ul>
                                <span v-else>N/A</span>
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Agent Representing Applicant</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <ul>
                                    <li>
                                        <span class="font-bold">NAME: </span>
                                        <span v-if="application.name_agent_representing">{{ application.name_agent_representing }}</span>
                                        <span v-else>N/A</span>
                                    </li>
                                    <li>
                                        <span class="font-bold">EMAIL: </span>
                                        <span v-if="application.email_agent_representing">{{ application.email_agent_representing }}</span>
                                        <span v-else>N/A</span>
                                    </li>
                                </ul>
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Rent</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <ul>
                                    <li>
                                        <span class="font-bold">Actual Rent: </span>
                                        <span v-if="property.rent">{{ property.formatted_rent }}</span>
                                        <span v-else>N/A</span>
                                    </li>
                                    <li>
                                        <span class="font-bold">Additional Rent I am willing to pay: </span>
                                        <span v-if="application.willing_rent">{{ application.formatted_willing_rent }}</span>
                                        <span v-else>N/A</span>
                                    </li>
                                </ul>
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Source of Income</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <div class="flow-root">
                                    <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
                                        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                                            <div class="overflow-hidden sm:rounded-lg border border-gray-200 rounded-lg shadow">
                                                <table class="min-w-full divide-y divide-gray-300">
                                                    <thead class="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 hidden lg:table-cell">Source of Income</th>
                                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell">Applicant Response</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody class="divide-y divide-gray-200 bg-white">
                                                    <tr class="hover:bg-blue-50">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6">Company Name
                                                            <dl class="font-normal lg:hidden">
                                                                <dd class="mt-1 truncate text-gray-700"><span v-if="application.company_name">{{ application.company_name }}</span><span v-else>N/A</span></dd>
                                                            </dl>
                                                        </td>
                                                        <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-700"><span v-if="application.company_name">{{ application.company_name }}</span><span v-else>N/A</span></td>
                                                    </tr>
                                                    <tr class="hover:bg-blue-50">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6">Position/Job Title
                                                            <dl class="font-normal lg:hidden">
                                                                <dd class="mt-1 truncate text-gray-700"><span v-if="application.job_title">{{ application.job_title }}</span><span v-else>N/A</span></dd>
                                                            </dl>
                                                        </td>
                                                        <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-700"><span v-if="application.job_title">{{ application.job_title }}</span><span v-else>N/A</span></td>
                                                    </tr>
                                                    <tr class="hover:bg-blue-50">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6">Annual Income
                                                            <dl class="font-normal lg:hidden">
                                                                <dd class="mt-1 truncate text-gray-700"><span v-if="application.annual_income">{{ application.annual_income }}</span><span v-else>N/A</span></dd>
                                                            </dl>
                                                        </td>
                                                        <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-700"><span v-if="application.annual_income">{{ application.annual_income }}</span><span v-else>N/A</span></td>
                                                    </tr>
                                                    <tr class="hover:bg-blue-50">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6">Supervisor Name
                                                            <dl class="font-normal lg:hidden">
                                                                <dd class="mt-1 truncate text-gray-700"><span v-if="application.supervisor_name">{{ application.supervisor_name }}</span><span v-else>N/A</span></dd>
                                                            </dl>
                                                        </td>
                                                        <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-700"><span v-if="application.supervisor_name">{{ application.supervisor_name }}</span><span v-else>N/A</span></td>
                                                    </tr>
                                                    <tr class="hover:bg-blue-50">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6">Supervisor Phone Number
                                                            <dl class="font-normal lg:hidden">
                                                                <dd class="mt-1 truncate text-gray-700"><span v-if="application.supervisor_phone">{{ application.supervisor_phone }}</span><span v-else>N/A</span></dd>
                                                            </dl>
                                                        </td>
                                                        <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-700"><span v-if="application.supervisor_phone">{{ application.supervisor_phone }}</span><span v-else>N/A</span></td>
                                                    </tr>
                                                    <tr class="hover:bg-blue-50">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6">Supervisor Email
                                                            <dl class="font-normal lg:hidden">
                                                                <dd class="mt-1 truncate text-gray-700"><span v-if="application.supervisor_email">{{ application.supervisor_email }}</span><span v-else>N/A</span></dd>
                                                            </dl>
                                                        </td>
                                                        <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-700"><span v-if="application.supervisor_email">{{ application.supervisor_email }}</span><span v-else>N/A</span></td>
                                                    </tr>
                                                    <tr class="hover:bg-blue-50">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6">How many months/years have you worked here?
                                                            <dl class="font-normal lg:hidden">
                                                                <dd class="mt-1 truncate text-gray-700"><span v-if="application.worked_experience">{{ application.worked_experience }}</span><span v-else>N/A</span></dd>
                                                            </dl>
                                                        </td>
                                                        <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-700"><span v-if="application.worked_experience">{{ application.worked_experience }}</span><span v-else>N/A</span></td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </dd>
                        </div>
                        <div v-if="application.user_incomes.length" class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Additional Sources of Income</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <div class="flow-root">
                                    <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
                                        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                                            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                                <table class="min-w-full divide-y divide-gray-300">
                                                    <thead class="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Income Type</th>
                                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Income Amount</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody class="divide-y divide-gray-200 bg-white">
                                                    <tr v-for="income in application.user_incomes" class="hover:bg-blue-50">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6">{{ income.type }}</td>
                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-700">${{ income.formatted_amount }}</td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Credit & Background Report</dt>
                            <dd v-if="(application.share_id)" class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <a v-if="!isSharedViewOnly" target="_blank" :href="route('agent.credit-report',application.id)" class="bg-rent-risk-blue-400 py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 w-full text-center">
                                    View Report <font-awesome-icon icon="fa-share-from-square" class="pl-3" />
                                </a>
                                <span v-else class="inline-flex items-center gap-1.5 bg-gray-100 py-2 px-4 rounded-md text-sm font-medium text-gray-400 cursor-not-allowed select-none">
                                    View Report <font-awesome-icon icon="fa-share-from-square" class="pl-1 opacity-50" />
                                </span>
                            </dd>
                            <dd v-else class="mt-1 text-sm leading-6 text-red-500 font-bold sm:col-span-2 sm:mt-0">
                                Applicant has not shared the report with you. Ask the applicant to log into their account and go to their Credit Report page. There is a green "Share Report" button at the top of the page. Ask the applicant to click this button and fill out the form on the following page.
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Desired Move-in Date</dt>
                            <dd v-if="(application.desired_move_in_date)" class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0 preserveLines" style="white-space: pre-wrap;">
                                {{ application.desired_move_in_date }}
                            </dd>
                            <dd v-else class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                N/A
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Desired Lease Term </dt>
                            <dd v-if="(application.desired_lease_term)" class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0 preserveLines" style="white-space: pre-wrap;">
                                {{ application.desired_lease_term }}
                            </dd>
                            <dd v-else class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                N/A
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Pets</dt>
                            <dd v-if="(application.pets)" class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0 preserveLines" style="white-space: pre-wrap;">
                                {{ application.pets }}
                            </dd>
                            <dd v-else class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                N/A
                            </dd>
                        </div>

                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Additional Occupants</dt>
                            <dd v-if="(application.additional_occupants)" class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0 preserveLines" style="white-space: pre-wrap;">
                                {{ application.additional_occupants }}
                            </dd>
                            <dd v-else class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                N/A
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Financial Documents</dt>
                            <dd v-if="documents" class="mt-2 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                                <ul role="list" class="divide-y divide-gray-100 rounded-md border border-gray-200">
                                    <li class="flex justify-end py-2 pl-4 pr-5 text-sm leading-6 text-right">
                                        <a :href="route('application.download-all-financial-documents', application.id)" target="_blank" class="primaryButton">Download All Files</a>
                                    </li>
                                    <li v-for="document in documents" class="flex items-center justify-between p-4 text-sm leading-6 hover:bg-blue-50">
                                        <div class="flex w-0 flex-1 items-center truncate">
                                            <a :href="route('application.download-documents', document.id)" target="_blank" class="inline-flex ">
                                                <PaperClipIcon class="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                                                <div class="ml-2 flex min-w-0 flex-1 gap-2">
                                                    <span class="font-medium">{{ document.file_name }} </span>
                                                </div>
                                            </a>
                                        </div>
                                        <div class="ml-4 flex-shrink-0">
                                            <a :href="route('application.view-documents', document.id)" target="_blank" class="font-medium text-indigo-600 hover:text-indigo-500">View</a>
                                            <a :href="route('application.download-documents', document.id)" target="_blank" class="font-medium text-indigo-600 hover:text-indigo-500 ml-4">Download</a>
                                        </div>
                                    </li>
                                </ul>
                                <small class="float-right text-red-700">Files older than 6 months will be deleted automatically</small>
                            </dd>
                            <dd v-else class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                Applicant has not uploaded any financial documents.
                            </dd>
                        </div>

                        <div v-if="main_applicant != null" class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Main Applicant</dt>
                            <dd class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <div class="flow-root">
                                    <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
                                        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                                            <div class="overflow-hidden sm:rounded-lg border border-gray-200 rounded-lg shadow">
                                                <table class="min-w-full divide-y divide-gray-300">
                                                    <thead class="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 hidden lg:table-cell">Name</th>
                                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell">Email</th>
                                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell">Status</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody class="divide-y divide-gray-200 bg-white">
                                                    <tr class="hover:bg-blue-50">
                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-700 font-bold lg:font-medium">
                                                            {{ main_applicant.name }}
                                                            <dl class="font-normal lg:hidden">
                                                                <dd class="mt-1 truncate text-gray-700">
                                                                    {{ main_applicant.email }}
                                                                </dd>
                                                                <dd class="mt-1 truncate text-gray-700">
                                                                    {{ main_applicant.status }}
                                                                </dd>
                                                            </dl>
                                                        </td>
                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-700 hidden lg:table-cell">{{ main_applicant.email }}</td>
                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-700 hidden lg:table-cell">{{ main_applicant.status }}</td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </dd>
                        </div>

                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Additional Applicants</dt>
                            <dd v-if="additional_applicants.length > 0" class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <div class="flow-root">
                                    <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
                                        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                                            <div class="overflow-hidden sm:rounded-lg border border-gray-200 rounded-lg shadow">
                                                <table class="min-w-full divide-y divide-gray-300">
                                                    <thead class="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 hidden lg:table-cell">Name</th>
                                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell">Email</th>
                                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell">Status</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody class="divide-y divide-gray-200 bg-white">
                                                    <tr v-for="(applicant, index) in additional_applicants" :key="index" class="hover:bg-blue-50">
                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-700 font-bold lg:font-medium">
                                                            {{ applicant.name }}
                                                            <dl class="font-normal lg:hidden">
                                                                <dd class="mt-1 truncate text-gray-700">
                                                                    {{ applicant.email }}
                                                                </dd>
                                                                <dd class="mt-1 truncate text-gray-700">
                                                                    {{ applicant.status }}
                                                                </dd>
                                                            </dl>
                                                        </td>
                                                        <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-700">{{ applicant.email }}</td>
                                                        <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-700">{{ applicant.status }}</td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </dd>
                            <dd v-else class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                N/A
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Vehicles Information</dt>
                            <dd v-if="application.vehicles.length > 0" class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <div class="flow-root">
                                    <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
                                        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                                            <div class="overflow-hidden sm:rounded-lg border border-gray-200 rounded-lg shadow">
                                                <table class="min-w-full divide-y divide-gray-300">
                                                    <thead class="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 hidden lg:table-cell">Model</th>
                                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell">Year</th>
                                                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell">Color</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody class="divide-y divide-gray-200 bg-white">
                                                    <tr v-for="(vehicle, index) in application.vehicles" :key="index" class="hover:bg-blue-50">
                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-700 font-bold lg:font-medium">
                                                            {{ vehicle.model }}
                                                            <dl class="font-normal lg:hidden">
                                                                <dd class="mt-1 truncate text-gray-700">
                                                                    {{ vehicle.year }}
                                                                </dd>
                                                                <dd class="mt-1 truncate text-gray-700">
                                                                    {{ vehicle.color }}
                                                                </dd>
                                                            </dl>
                                                        </td>
                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-700 hidden lg:table-cell">{{ vehicle.year }}</td>
                                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-700 hidden lg:table-cell">{{ vehicle.color }}</td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </dd>
                            <dd v-else class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                N/A
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Additional Information</dt>
                            <dd v-if="(application.additional_information)" class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0 preserveLines" style="white-space: pre-wrap;">
                                {{ application.additional_information }}
                            </dd>
                            <dd v-else class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                N/A
                            </dd>
                        </div>
                        <div class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900"><span v-if="property.require_two_step">Credit Report Release Authorization</span><span v-else>Authorization Signature</span></dt>
                            <dd v-if="(application.share_id)" class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <div class="text-sm">I acknowledge the conditions in this Application and authorize the Listing Broker or landlord processing this Application to verify any information contained herein and to perform any credit or investigative inquiries necessary to properly evaluate this Application, to include rental history verification, and to share with Landlord or designated representative the information necessary to make a determination.</div>
                                <img alt="Authorization Signature" class="h-20 w-auto" v-bind:src="application.authorization_signature" />
                            </dd>
                            <dd v-else class="mt-1 text-sm leading-6 text-red-500 font-bold sm:col-span-2 sm:mt-0">
                                Applicant has not shared the report with you. Ask the applicant to log into their account and go to their Credit Report page. There is a green "Share Report" button at the top of the page. Ask the applicant to click this button and fill out the form on the following page.
                            </dd>
                        </div>
                        <div v-if="property.require_two_step" class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt class="text-sm font-semibold leading-6 text-gray-900">Criminal Background Release Authorization</dt>
                            <dd v-if="(application.tenant_approves_criminal_background)" class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                <div class="text-sm">I acknowledge the conditions in this Application and authorize the Listing Broker or landlord processing this Application to verify any information contained herein and to perform any credit or investigative inquiries necessary to properly evaluate this Application, to include rental history verification, and to share with Landlord or designated representative the information necessary to make a determination.</div>
                                <img alt="Authorization Signature" class="h-20 w-auto" v-bind:src="application.background_report_authorization_signature" />
                            </dd>
                            <dd v-else class="mt-1 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                                Applicant has not shared the report with you.
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>
        </div>
<!--        Leasing Decision Worksheet-->
        <div v-if="application.is_shared" class="mt-8 w-full mx-auto grid grid-cols-1 lg:grid-cols-6">
            <div class="space-y-6 lg:col-start-1 lg:col-span-6 bg-white shadow sm:rounded-lg p-8">

                <div class="flex justify-between items-center">
                    <div class="theme_color p-2 w-full rounded py-4 pb-4">
                        <h3 class="text-2xl font-semibold leading-7 text-white ml-2">Leasing Decision Worksheet</h3>
                    </div>
                </div>
                <div class="flex justify-between items-center">
                    <div class="p-2 w-4/5 rounded">
                        <p class="mt-1 max-w-2xl text-sm leading-6">Fill out and save the worksheet below for your records, or click the print button to print it.</p>
                    </div>
                    <div class="flex gap-2 bg-white">
                        <a :href="route('leasing-worksheet.download',[property.id, application.id])" target="_blank" class="py-2 px-4 mt-3 rounded-md shadow-sm text-sm font-medium text-white bg-rent-risk-blue-400 hover:bg-rent-risk-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 w-full text-center h-full flex items-center">Print</a>
                    </div>
                </div>

                <div class="mt-6">
                    <div class="max-w-full overflow-x-auto">
                        <table class="min-w-full border border-gray-300">
                            <tbody>
                            <tr>
                                <th class="border border-gray-300 p-4 text-left font-bold bg-gray-100 w-4/5">
                                    Credit Score (VantageScore)
                                </th>
                                <td class="border border-gray-300 p-4 text-left">
                                    {{application.credit_score}}
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2" class="border border-gray-300 p-4 text-left">
                                    <ul class="space-y-2 text-gray-700 list-disc list-inside dark:text-gray-400">
                                        <li>Scores range between 300 - 850.</li>
                                        <li>A credit score of 661 or above is good.</li>
                                        <li>A score of 781 or above is excellent.</li>
                                        <li>Most consumers have credit scores between 600 and 750.</li>
                                    </ul>
                                    <p class="mt-6 font-bold">Examine credit for behavior patterns: (Review the Credit & Background Report)</p>
                                    <ul class="space-y-2 text-gray-700 list-disc list-inside dark:text-gray-400">
                                        <li class="flex items-center">
                                            <div class="w-4/5">
                                                <span class="mr-2 font-bold text-lg">&#8226;</span>
                                                Any late payments?
                                            </div>
                                            <span class="ml-6 flex items-center gap-x-4">
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-late-payments-yes" v-model="leasingWorksheetForm.has_late_payments" :value="true" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-late-payments-yes" class="text-sm font-medium text-gray-900">Yes</label>
                                                </span>
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-late-payments-no" v-model="leasingWorksheetForm.has_late_payments" :value="false" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-late-payments-no" class="text-sm font-medium text-gray-900">No</label>
                                                </span>
                                            </span>
                                        </li>
                                        <li class="flex items-center">
                                            <div class="w-4/5">
                                                <span class="mr-2 font-bold text-lg">&#8226;</span>
                                                Consumer accounts with high balances?
                                            </div>
                                            <span class="ml-6 flex items-center gap-x-4">
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-high-balances-yes" v-model="leasingWorksheetForm.has_high_balance_accounts" :value="true" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-high-balances-yes" class="text-sm font-medium text-gray-900">Yes</label>
                                                </span>
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-high-balances-no" v-model="leasingWorksheetForm.has_high_balance_accounts" :value="false" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-high-balances-no" class="text-sm font-medium text-gray-900">No</label>
                                                </span>
                                            </span>
                                        </li>
                                        <li class="flex items-center">
                                            <div class="w-4/5">
                                                <span class="mr-2 font-bold text-lg">&#8226;</span>
                                                How many accounts in collection?
                                            </div>
                                            <span class="ml-6 flex items-center gap-x-2">
                                                <label for="worksheet-collections-total" class="text-sm font-medium text-gray-900">Total:</label>
                                                <input id="worksheet-collections-total" v-model.number="leasingWorksheetForm.accounts_in_collection_total" :disabled="isSharedViewOnly" type="number" min="0" max="999" class="w-20 rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100" />
                                            </span>
                                        </li>
                                        <li class="flex items-center">
                                            <div class="w-4/5">
                                                <span class="mr-2 font-bold text-lg">&#8226;</span>
                                                Liens or judgments?
                                            </div>
                                            <span class="ml-6 flex items-center gap-x-4">
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-liens-yes" v-model="leasingWorksheetForm.has_liens_or_judgments" :value="true" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-liens-yes" class="text-sm font-medium text-gray-900">Yes</label>
                                                </span>
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-liens-no" v-model="leasingWorksheetForm.has_liens_or_judgments" :value="false" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-liens-no" class="text-sm font-medium text-gray-900">No</label>
                                                </span>
                                            </span>
                                        </li>
                                    </ul>
                                    <p v-if="leasingWorksheetForm.errors.accounts_in_collection_total" class="mt-2 text-sm text-red-600">{{ leasingWorksheetForm.errors.accounts_in_collection_total }}</p>
                                </td>
                            </tr>
                            <tr v-if="!property.require_two_step || (property.require_two_step && application.tenant_approves_criminal_background)">
                                <th class="border border-gray-300 p-4 text-left font-bold bg-gray-100 w-4/5">
                                    Criminal Background Report: (Review the Credit & Background Report)
                                </th>
                                <td class="border border-gray-300 p-4 text-left">
                                    <span v-if="application.convictions_count === null" class="text-yellow-700 font-semibold">Search unavailable</span>
                                    <template v-else>{{application.convictions_count}}</template>
                                </td>
                            </tr>
                            <tr v-if="!property.require_two_step || (property.require_two_step && application.tenant_approves_criminal_background)">
                                <td colspan="2" class="border border-gray-300 p-4 text-left">
                                    <ul class="space-y-2 text-gray-700 list-disc list-inside dark:text-gray-400">
                                        <li class="flex items-center">
                                            <div class="w-4/5">
                                                <span class="mr-2 font-bold text-lg">&#8226;</span>
                                                Any previous conviction(s) on record?
                                            </div>
                                            <span class="ml-6 flex items-center gap-x-4">
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-convictions-yes" v-model="leasingWorksheetForm.has_previous_convictions" :value="true" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-convictions-yes" class="text-sm font-medium text-gray-900">Yes</label>
                                                </span>
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-convictions-no" v-model="leasingWorksheetForm.has_previous_convictions" :value="false" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-convictions-no" class="text-sm font-medium text-gray-900">No</label>
                                                </span>
                                            </span>
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                            <tr v-if="!property.require_two_step || (property.require_two_step && application.tenant_approves_criminal_background)">
                                <th class="border border-gray-300 p-4 text-left font-bold bg-gray-100 w-4/5">
                                    Court Housing Record: (Review the Credit & Background Report)
                                </th>
                                <td class="border border-gray-300 p-4 text-left">
                                    <span v-if="application.evictions_count === null" class="text-yellow-700 font-semibold">Search unavailable</span>
                                    <template v-else>{{application.evictions_count}}</template>
                                </td>
                            </tr>
                            <tr v-if="!property.require_two_step || (property.require_two_step && application.tenant_approves_criminal_background)">
                                <td colspan="2" class="border border-gray-300 p-4 text-left">
                                    <ul class="space-y-2 text-gray-700 list-disc list-inside dark:text-gray-400">
                                        <li class="flex items-center">
                                            <div class="w-4/5">
                                                <span class="mr-2 font-bold text-lg">&#8226;</span>
                                                Any previous eviction(s) on record?
                                            </div>
                                            <span class="ml-6 flex items-center gap-x-4">
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-evictions-yes" v-model="leasingWorksheetForm.has_previous_evictions" :value="true" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-evictions-yes" class="text-sm font-medium text-gray-900">Yes</label>
                                                </span>
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-evictions-no" v-model="leasingWorksheetForm.has_previous_evictions" :value="false" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-evictions-no" class="text-sm font-medium text-gray-900">No</label>
                                                </span>
                                            </span>
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                            <tr>
                                <th class="border border-gray-300 p-4 text-left font-bold bg-gray-100 w-4/5">
                                    Estimated Income
                                </th>
                                <td class="border border-gray-300 p-4 text-left">
                                    <span class="flex items-center gap-x-1">
                                        <label for="worksheet-estimated-income" class="text-sm font-medium text-gray-900">$</label>
                                        <input id="worksheet-estimated-income" v-model.number="leasingWorksheetForm.estimated_income" :disabled="isSharedViewOnly" type="number" min="0" step="0.01" class="w-32 rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100" />
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2" class="border border-gray-300 p-4 text-left">
                                    <ul class="space-y-2 text-gray-700 list-disc list-inside dark:text-gray-400">
                                        <li>Low Risk is when income is 3X monthly rent amount</li>
                                        <li>Med Risk is when income is 2.5X monthly rent amount</li>
                                        <li>High Risk is when income is 2X monthly rent amount</li>
                                    </ul>
                                    <p v-if="leasingWorksheetForm.errors.estimated_income" class="mt-2 text-sm text-red-600">{{ leasingWorksheetForm.errors.estimated_income }}</p>
                                </td>
                            </tr>
                            <tr>
                                <th class="border border-gray-300 p-4 text-left font-bold bg-gray-100 w-4/5">
                                    Rental History
                                </th>
                                <td class="border border-gray-300 p-4 text-left">
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2" class="border border-gray-300 p-4 text-left">
                                    <p class="mb-2">When contacting the previous landlord, ask the following:</p>
                                    <ol class="space-y-2 text-gray-700 dark:text-gray-400">
                                        <li class="flex items-center">
                                            <div class="w-4/5">
                                                <span class="mr-2 font-bold">1.</span>
                                                Did the tenant pay monthly rent on time?
                                            </div>
                                            <span class="ml-6 flex items-center gap-x-4">
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-paid-on-time-yes" v-model="leasingWorksheetForm.paid_rent_on_time" :value="true" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-paid-on-time-yes" class="text-sm font-medium text-gray-900">Yes</label>
                                                </span>
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-paid-on-time-no" v-model="leasingWorksheetForm.paid_rent_on_time" :value="false" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-paid-on-time-no" class="text-sm font-medium text-gray-900">No</label>
                                                </span>
                                            </span>
                                        </li>
                                        <li class="flex items-center">
                                            <div class="w-4/5">
                                                <span class="mr-2 font-bold">2.</span>
                                                Did the tenant maintain the property?
                                            </div>
                                            <span class="ml-6 flex items-center gap-x-4">
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-maintained-yes" v-model="leasingWorksheetForm.maintained_property" :value="true" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-maintained-yes" class="text-sm font-medium text-gray-900">Yes</label>
                                                </span>
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-maintained-no" v-model="leasingWorksheetForm.maintained_property" :value="false" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-maintained-no" class="text-sm font-medium text-gray-900">No</label>
                                                </span>
                                            </span>
                                        </li>
                                        <li class="flex items-center">
                                            <div class="w-4/5">
                                                <span class="mr-2 font-bold">3.</span>
                                                Would you rent to the tenants again?
                                            </div>
                                            <span class="ml-6 flex items-center gap-x-4">
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-rent-again-yes" v-model="leasingWorksheetForm.would_rent_again" :value="true" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-rent-again-yes" class="text-sm font-medium text-gray-900">Yes</label>
                                                </span>
                                                <span class="flex items-center gap-x-2">
                                                    <input id="worksheet-rent-again-no" v-model="leasingWorksheetForm.would_rent_again" :value="false" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                                    <label for="worksheet-rent-again-no" class="text-sm font-medium text-gray-900">No</label>
                                                </span>
                                            </span>
                                        </li>
                                    </ol>
                                </td>
                            </tr>
                            <tr>
                                <th class="border border-gray-300 p-4 text-left font-bold bg-gray-100 w-4/5">
                                    Leasing Decision: (Landlord or Real Estate Agent Will Make Leasing Decision)
                                </th>
                                <td class="border border-gray-300 p-4 text-left">
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2" class="border border-gray-300 p-4 text-left">
                                    <p class="mb-2">Based on the information above Landlord or Agent:</p>
                                    <ul class="space-y-2 text-gray-700 list-inside dark:text-gray-400">
                                        <li class="flex items-center gap-x-2">
                                            <input id="worksheet-recommends-yes" v-model="leasingWorksheetForm.recommends_applicant" :value="true" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                            <label for="worksheet-recommends-yes" class="text-sm font-medium text-gray-900">Recommends this applicant</label>
                                        </li>
                                        <li class="flex items-center gap-x-2">
                                            <input id="worksheet-recommends-no" v-model="leasingWorksheetForm.recommends_applicant" :value="false" :disabled="isSharedViewOnly" type="radio" :class="worksheetRadioClass" />
                                            <label for="worksheet-recommends-no" class="text-sm font-medium text-gray-900">Does not recommend this applicant</label>
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div v-if="!isSharedViewOnly" class="mt-6 flex justify-end items-center gap-4">
                    <span v-if="leasingWorksheetForm.recentlySuccessful" class="text-sm font-medium text-green-600">
                        <font-awesome-icon :icon="faCircleCheck" aria-hidden="true" /> Saved
                    </span>
                    <loading-button :form="leasingWorksheetForm" class="py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white bg-rent-risk-blue-400 hover:bg-rent-risk-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400" @click="saveLeasingWorksheet">Save Worksheet</loading-button>
                </div>
                <div class="mt-6">
                    <strong>Disclaimer:</strong> Consider additional information provided by prospective tenant when making leasing decision.
                </div>
            </div>
        </div>
        <div v-if="showAcceptModal">
            <TransitionRoot as="template" :show="showAcceptModal">
                <Dialog as="div" class="relative z-50" @close="showAcceptModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <CheckIcon class="h-6 w-6 text-green-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 sm:mt-5">
                                            <DialogTitle as="h3" class="text-base text-center font-semibold leading-6 text-gray-900">Are you sure you want to accept this application?</DialogTitle>
                                            <div class="mt-2">
                                                <div class="rounded bg-yellow-50 border border-t-yellow-800 border-t-4 border-yellow-400 py-4 px-2">
                                                    <div class="flex items-center">
                                                        <div class="flex-shrink-0">
                                                            <font-awesome-icon :icon="faInfoCircle" class="text-yellow-700" aria-hidden="true" /> Back
                                                        </div>
                                                        <div class="ml-3 flex-1 md:flex md:justify-between">
                                                            <p class="text-sm text-yellow-700">
                                                                An email with the following message will automatically be sent to you and the applicant when you Accept the Application.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <p class="mt-4" v-html="accept_message"></p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showAcceptModal = false">Cancel</button>
                                        <button type="button" class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600" @click="accept_decline(1)">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
        <div v-if="showDeclineModal">
            <TransitionRoot as="template" :show="showDeclineModal">
                <Dialog as="div" class="relative z-50" @close="showDeclineModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                            <XIcon class="h-6 w-6 text-red-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">
                                                You have selected to decline the application for <span class="font-bold underline">{{application.user.name}}</span>. Choose a reason below for declining the application.
                                            </DialogTitle>
                                            <div class="mt-2">
                                                <div class="rounded bg-yellow-50 border border-t-yellow-800 border-t-4 border-yellow-400 py-4 px-2">
                                                    <div class="flex items-center">
                                                        <div class="flex-shrink-0">
                                                            <font-awesome-icon :icon="faInfoCircle" class="text-yellow-700" aria-hidden="true" /> Back
                                                        </div>
                                                        <div class="ml-3 flex-1 md:flex md:justify-between">
                                                            <p class="text-sm text-yellow-700">
                                                                An email with the following message will automatically be sent to you and the applicant when you Decline the Application.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="rounded-lg shadow-lg border border-gray-300 my-3 p-4">
                                                    <h2 class="mt-4 text-2xl uppercase">Decline Other</h2>
                                                    <p class="my-4 text-left" v-html="decline_other_message"></p>
                                                    <button type="button" class="flex-shrink-0 w-full mb-2 justify-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rent-risk-blue-400" @click="accept_decline(2)">Decline Other</button>
                                                </div>
                                                <div class="rounded-lg shadow-lg border border-gray-300 my-3 p-4">
                                                    <h2 class="mt-4 text-2xl uppercase">Decline Financial</h2>
                                                    <p class="my-4 text-left" v-html="decline_financial_message"></p>
                                                    <button type="button" class="flex-shrink-0 w-full mb-2 justify-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rent-risk-blue-400" @click="accept_decline(3)">Decline Financial/Credit</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6">
                                        <button type="button" class="flex-shrink-0 w-full mb-2 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showDeclineModal = false">Cancel</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
        <div class="fixed bottom-24 right-6">
            <button @click="scrollToTop()" v-show="scY > 300" class="bg-rent-risk-blue-400 p-2 rounded-lg">
                <font-awesome-icon :icon="faChevronUp" class="text-white" aria-hidden="true" />
            </button>
        </div>

        <!-- Enhanced Screening Options Modal -->
        <enhanced-screening-options-modal
            ref="enhancedScreeningModal"
            :show="showEnhancedScreeningOptionsModal"
            :application="application"
            :addons="addons"
            :has-protect-access="hasProtectAccess"
            :subscription-type="subscriptionType"
            :free-bundles-used="freeBundlesUsed"
            :free-eso-offer-used="freeEsoOfferUsed"
            :payment-method-success="payment_method_success"
            @close="closeEnhancedScreeningOptionsModal"
            @submit="handleEnhancedScreeningSubmit"
        />

        <!-- Add Credit Card Modal -->
        <add-card-popup 
            :show="showAddCardModal" 
            :stripe_data="stripe_data" 
            @card-added="handleCardAdded" 
            @close="closeCardModal()"
        />

        <!-- Enhanced Screening Results Modal -->
        <enhanced-screening-results-modal
            :show="showEnhancedScreeningResultsModal"
            :application="application"
            :property="property"
            @close="showEnhancedScreeningResultsModal = false"
        />

        <!-- Subscription Protection Component -->
        <subscription-protection
            ref="subscriptionProtection"
            :subscription-type="'protect'"
            :terms-accepted="protect_tos_accepted_at"
            :has-payment-method="is_agent_payment_method_added"
            :user-payment-method="user_payment_method"
            :stripe-data="stripe_data"
            :show-confirmation-modal="showConfirmationForSubscription"
            @success="handleSubscriptionSuccess"
            @error="handleSubscriptionError"
            @cancel="handleSubscriptionCancel"
        />
    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import TrashedMessage from '@/Shared/TrashedMessage.vue'
import ProgressRing from '@/Shared/ProgressRing.vue'
import financialDocuments from "../Applications/FinancialDocuments.vue";
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    MenuButton,
    Menu,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot
} from '@headlessui/vue'
import ToolTip from "@/Shared/ToolTip.vue";
import AlertCard from "@/Shared/AlertCard.vue";
import EnhancedScreeningOptionsModal from '@/Components/EnhancedScreeningOptionsModal.vue';
import EnhancedScreeningResultsModal from '@/Components/EnhancedScreeningResultsModal.vue';
import AddCardPopup from '@/Shared/AddCardPopup.vue';
import SubscriptionProtection from '@/Shared/V2/SubscriptionProtection.vue';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faArrowUpRightFromSquare, faChevronLeft, faChevronUp, faCircleX, faInfoCircle, faPenToSquare, faShieldCheck, faSquareChevronLeft, faBoxArchive, faChevronDown, faUserCheck, faCircleCheck, faCloudArrowDown, faArrowsRotate, faFiles } from "@fortawesome/pro-solid-svg-icons";
import { faCheckCircle, faXmark } from "@fortawesome/pro-light-svg-icons";

export default {
    props: {
        property: Object,
        documents: Object,
        application: Object,
        additional_applicants: Object,
        main_applicant: Object,
        accept_message: String,
        decline_other_message: String,
        decline_financial_message: String,
        experian_attempt: Object,
        addons: Object,
        hasProtectAccess: Boolean,
        subscriptionType: String,
        freeBundlesUsed: Number,
        freeEsoOfferUsed: Boolean,
        shouldAddonsBeFree: Boolean,
        user_payment_method: Object,
        stripe_data: Array,
        is_agent_payment_method_added: Boolean,
        protect_tos_accepted_at: Boolean,
        property_access: {
            type: String,
            default: 'owner',
        },
    },
    computed: {
        financialDocuments() {
            return financialDocuments
        },
        isSharedViewOnly() {
            return this.property_access === 'shared_view'
        },
        referenceVerifications() {
            return this.application.reference_verifications || [];
        },
    },
    components: {
        ToolTip,
        Menu,
        MenuItem,
        MenuButton,
        MenuItems,
        TailwindLayout,
        LoadingButton,
        SelectInput,
        TrashedMessage,
        Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot,
        FontAwesomeIcon,
        AlertCard,
        EnhancedScreeningOptionsModal,
        EnhancedScreeningResultsModal,
        AddCardPopup,
        SubscriptionProtection
    },
    data() {
        return {
            showAcceptModal : false,
            showDeclineModal : false,
            showEnhancedScreeningOptionsModal: false,
            showEnhancedScreeningResultsModal: false,
            showAddCardModal: false,
            scY: 0,
            scTimer: 0,
            faSquareChevronLeft,
            faChevronLeft,
            faInfoCircle,
            faXmark,
            faPenToSquare,
            faArrowUpRightFromSquare,
            faShieldCheck,
            faBoxArchive,
            faCircleX,
            faChevronDown,
            faChevronUp,
            faUserCheck,
            faCircleCheck,
            faCheckCircle,
            faCloudArrowDown,
            faArrowsRotate,
            faFiles,
            
            enhancedScreeningForm: this.$inertia.form({
                addons: [],
            }),
            leasingWorksheetForm: this.$inertia.form({
                has_late_payments: this.application.leasing_worksheet?.has_late_payments ?? null,
                has_high_balance_accounts: this.application.leasing_worksheet?.has_high_balance_accounts ?? null,
                accounts_in_collection_total: this.application.leasing_worksheet?.accounts_in_collection_total ?? null,
                has_liens_or_judgments: this.application.leasing_worksheet?.has_liens_or_judgments ?? null,
                has_previous_convictions: this.application.leasing_worksheet?.has_previous_convictions ?? null,
                has_previous_evictions: this.application.leasing_worksheet?.has_previous_evictions ?? null,
                estimated_income: this.application.leasing_worksheet?.estimated_income ?? null,
                paid_rent_on_time: this.application.leasing_worksheet?.paid_rent_on_time ?? null,
                maintained_property: this.application.leasing_worksheet?.maintained_property ?? null,
                would_rent_again: this.application.leasing_worksheet?.would_rent_again ?? null,
                recommends_applicant: this.application.leasing_worksheet?.recommends_applicant ?? null,
            }),
            worksheetRadioClass: 'relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden',
            payment_method: this.user_payment_method,
            payment_method_success: null,
            pendingEnhancedScreeningData: null,
            isSubscribingToProtect: false,
            isInSubscriptionFlow: false,
            showConfirmationForSubscription: false
        }
    },
    mounted() {
        window.addEventListener('scroll', this.handleScroll);
    },
    methods: {
        referenceTypeLabel(type) {
            const labels = {
                current_landlord: 'Current Landlord',
                previous_landlord: 'Previous Landlord',
                real_estate_agent: 'Current Real Estate Agent',
            };

            return labels[type] || type;
        },
        landlordVerification(referenceType) {
            return this.referenceVerifications.find((verification) => verification.reference_type === referenceType);
        },
        landlordQuestionnaireStatus(referenceType) {
            const verification = this.landlordVerification(referenceType);

            if (!verification) {
                return null;
            }

            if (verification.submitted_at) {
                return 'Completed Questionnaire ✅';
            }

            return 'Awaiting questionnaire';
        },
        formatYesNo(value) {
            if (value === true) {
                return 'Yes';
            }

            if (value === false) {
                return 'No';
            }

            return 'N/A';
        },
        formatSubmittedAt(value) {
            if (!value) {
                return '';
            }

            return new Date(value).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            });
        },
        archive() {
            this.$inertia.get(this.route('properties.application.archive', [this.property.id, this.application.id]), {
                onFinish: () => {
                    this.showArchiveModal = false;
                }
            })
        },
        restore() {
            this.$inertia.get(this.route('properties.application.restore', [this.property.id, this.application.id]), {}, {
                onFinish: () => {
                    this.showRestoreModal = false;
                }
            })
        },
        handleScroll: function () {
            if (this.scTimer) return;
            this.scTimer = setTimeout(() => {
                this.scY = window.scrollY;
                clearTimeout(this.scTimer);
                this.scTimer = 0;
            }, 100);
        },
        toTop: function () {
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            });
        },
        scrollToTop() {
            // Scroll to the top of the page
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            this.scY = window.scrollY;
        },
        openAcceptModal() {
            // RAC-9997: dropped `this.application = application` — application
            // is a Vue prop (read-only proxy) so writing to it throws
            // RENT-RISK-9T. The prop already provides what accept_decline()
            // needs to read.
            this.showAcceptModal = true;
        },
        openDeclineModal() {
            this.showDeclineModal = true;
        },
        accept_decline(value) {
            this.$inertia.post(this.route('applications.accept', this.application.id), {accept_decline: value}, {
                onFinish: () => {
                    this.showAcceptModal = false;
                    this.showDeclineModal = false;
                }
            })
        },
        openEnhancedScreeningOptionsModal() {
            this.showEnhancedScreeningOptionsModal = true;
        },
        openEnhancedScreeningResultsModal() {
            this.showEnhancedScreeningResultsModal = true;
        },
        closeEnhancedScreeningOptionsModal() {
            this.enhancedScreeningForm.addons = [];
            this.showEnhancedScreeningOptionsModal = false;
        },
        handleEnhancedScreeningSubmit(data) {
            // Store the selected addons for later use when payment card is added
            this.pendingEnhancedScreeningData = data;

            if(this.payment_method === null) {
                this.showAddCardModal = true;
                return false;
            }

            this.processEnhancedScreeningPayment(data);
        },
        processEnhancedScreeningPayment(data) {
            this.enhancedScreeningForm.addons = data.addons;
            this.enhancedScreeningForm.put(this.route('properties.application.required_addons', [this.property.id, this.application.id]), {
                onFinish: () => {
                    this.showEnhancedScreeningOptionsModal = false;
                    this.enhancedScreeningForm.addons = []; // setting back to empty
                    this.pendingEnhancedScreeningData = null; // clear pending data
                }
            })
        },
        saveLeasingWorksheet() {
            this.leasingWorksheetForm.put(this.route('properties.application.leasing-worksheet.save', [this.property.id, this.application.id]), {
                preserveScroll: true,
            })
        },
        handleCardAdded({ payment_method, success }) {
            if(success) {
                this.payment_method = payment_method.payment_method;
                this.showAddCardModal = false;
                this.payment_method_success = payment_method.message;
                setTimeout(() => {
                    this.payment_method_success = null;
                }, 3000);

                // If there's pending enhanced screening data, process it now
                if (this.pendingEnhancedScreeningData) {
                    this.processEnhancedScreeningPayment(this.pendingEnhancedScreeningData);
                }
            }
        },
        closeCardModal() {
            this.showAddCardModal = false;
        },
        handleSubscriptionSuccess({ pendingApplication }) {
            this.payment_method_success = 'Successfully subscribed to RiskGuard!';
            setTimeout(() => this.payment_method_success = null, 5000);

            this.isInSubscriptionFlow = false;
            this.isSubscribingToProtect = false;
            this.showConfirmationForSubscription = false;

            if (this.$refs.enhancedScreeningModal) {
                this.$refs.enhancedScreeningModal.selectAllAddons();
            }
        },
        handleSubscriptionError({ type, error }) {
            alert(error || 'An error occurred. Please try again.');
            this.isInSubscriptionFlow = false;
            this.isSubscribingToProtect = false;
            this.showConfirmationForSubscription = false;
        },
        handleSubscriptionCancel() {
            this.isInSubscriptionFlow = false;
            this.isSubscribingToProtect = false;
            this.showConfirmationForSubscription = false;
        },
    }
}
</script>
